/**
 * @file     lenderCeiling.ts
 * @purpose  Turns a stated income and down payment into the purchase price a
 *           lender would underwrite, so the buyer-facing surfaces can filter on
 *           a real ceiling instead of an asking price.
 */
import {
  AMORTIZATION_MONTHS,
  GDS_RATIO,
  ILLUSTRATIVE_CONTRACT_RATE,
  TDS_RATIO,
  TYPICAL_CONTRACT_RATE,
  amortizedPayment,
  canadianPeriodicRate,
  stressTestRate,
} from './mortgage';
import { INSURED_MORTGAGE_PRICE_CAP } from './downPayment';
import { CMHC_PREMIUM_BY_LTV, mortgageInsurance } from './mortgageInsurance';
import {
  DEFAULT_HEAT_MONTHLY,
  DEFAULT_PROPERTY_TAX_ANNUAL_RATE,
  STRATA_INCLUSION,
  qualifyingHousingCost,
} from './housingCost';

/**
 * The carrying costs a lender counts alongside principal and interest. Strata
 * enters at half its monthly amount, which is the underwriting convention, and
 * property tax is a rate against the purchase price rather than a flat figure,
 * so the ceiling stays self-consistent as the price moves.
 */
export interface CarryingCostAssumptions {
  strataMonthly: number;
  heatMonthly: number;
  propertyTaxAnnualRate: number;
}

export const DEFAULT_CARRYING_COSTS: CarryingCostAssumptions = {
  strataMonthly: 450,
  heatMonthly: DEFAULT_HEAT_MONTHLY,
  propertyTaxAnnualRate: DEFAULT_PROPERTY_TAX_ANNUAL_RATE,
};

export { STRATA_INCLUSION };

export interface LenderCeilingInput {
  monthlyGrossIncome: number;
  downPayment: number;
  /** Monthly payments on other debt. Drives the TDS test. */
  monthlyDebt?: number;
  contractRate?: number;
  amortizationMonths?: number;
  carrying?: Partial<CarryingCostAssumptions>;
}

export interface LenderCeiling {
  /** The most a lender would underwrite, before closing costs. */
  purchasePrice: number;
  /** The loan, including any insurance premium added to it. */
  mortgage: number;
  /** Mortgage insurance added to the loan when the down payment is under 20%. */
  insurancePremium: number;
  /** Monthly principal and interest at the qualifying rate. */
  qualifyingPayment: number;
  /** Everything the lender counts as housing cost, at the qualifying rate. */
  housingPayment: number;
  /** Which test bound the result. TDS only binds when other debt is present. */
  boundBy: 'gds' | 'tds';
  contractRate: number;
  qualifyingRate: number;
  amortizationMonths: number;
  carrying: CarryingCostAssumptions;
}

/**
 * Solves for the purchase price whose full housing cost exactly consumes the
 * allowance. Property tax scales with the price, so the identity is solved in
 * closed form rather than by fitting a flat tax figure:
 *
 *   (price - down) * factor + strata * 0.5 + price * taxRate / 12 + heat = allowance
 */
function priceForAllowance(
  allowance: number,
  downPayment: number,
  paymentFactor: number,
  carrying: CarryingCostAssumptions,
  premiumRate = 0,
): number {
  const fixedCosts = carrying.strataMonthly * STRATA_INCLUSION + carrying.heatMonthly;
  const monthlyTaxRate = carrying.propertyTaxAnnualRate / 12;
  const loanFactor = paymentFactor * (1 + premiumRate);
  const numerator = allowance - fixedCosts + downPayment * loanFactor;
  const denominator = loanFactor + monthlyTaxRate;
  if (denominator <= 0) return downPayment;
  return numerator / denominator;
}

/** Highest price a down payment legally covers (5% to $500k, 10% above). */
function maxPriceForDownPayment(downPayment: number): number {
  if (downPayment <= 25_000) return downPayment / 0.05;
  const price = 500_000 + (downPayment - 25_000) / 0.1;
  // Past the insured cap the rule becomes 20% down.
  return price < INSURED_MORTGAGE_PRICE_CAP
    ? price
    : Math.max(INSURED_MORTGAGE_PRICE_CAP - 1, downPayment / 0.2);
}

/**
 * Under 20% down the insurance premium is added to the loan, and its rate
 * depends on the loan-to-value, which depends on the price being solved for.
 * Solve once per premium rate and keep the answer whose own premium matches
 * the rate it was solved with; a price that lands in a different band is not
 * self-consistent. The highest consistent price is the ceiling.
 */
function insuredPrice(
  allowance: number,
  downPayment: number,
  paymentFactor: number,
  carrying: CarryingCostAssumptions,
): number {
  const rates = [0, ...CMHC_PREMIUM_BY_LTV.map((band) => band.rate)];
  let best = 0;
  for (const rate of rates) {
    const price = priceForAllowance(allowance, downPayment, paymentFactor, carrying, rate);
    if (price <= downPayment) continue;
    if (mortgageInsurance(price, downPayment).premiumRate === rate) best = Math.max(best, price);
  }
  // Band edges can leave no exact match; the uninsured-rate solve with the
  // real premium applied is then the safe, lower answer.
  if (best === 0) {
    const guess = priceForAllowance(allowance, downPayment, paymentFactor, carrying);
    const rate = mortgageInsurance(guess, downPayment).premiumRate;
    best = priceForAllowance(allowance, downPayment, paymentFactor, carrying, rate);
  }
  return best;
}

/**
 * The ceiling a lender underwrites to. Pure and client-safe on purpose: it runs
 * in the browser beside the filter chips, so it must not reach the MLS layer.
 */
export function lenderCeiling(input: LenderCeilingInput): LenderCeiling {
  const contractRate = input.contractRate ?? TYPICAL_CONTRACT_RATE;
  const qualifyingRate = stressTestRate(contractRate);
  const amortizationMonths = input.amortizationMonths ?? AMORTIZATION_MONTHS;
  const carrying: CarryingCostAssumptions = { ...DEFAULT_CARRYING_COSTS, ...input.carrying };
  const monthlyDebt = Math.max(0, input.monthlyDebt ?? 0);
  const income = Math.max(0, input.monthlyGrossIncome);
  const downPayment = Math.max(0, input.downPayment);

  const gdsAllowance = income * GDS_RATIO;
  const tdsAllowance = income * TDS_RATIO - monthlyDebt;
  const boundBy: 'gds' | 'tds' = tdsAllowance < gdsAllowance ? 'tds' : 'gds';
  const allowance = Math.max(0, Math.min(gdsAllowance, tdsAllowance));

  const periodicRate = canadianPeriodicRate(qualifyingRate, 12);
  const paymentFactor =
    periodicRate === 0
      ? 1 / amortizationMonths
      : periodicRate / (1 - Math.pow(1 + periodicRate, -amortizationMonths));

  const rawPrice = insuredPrice(allowance, downPayment, paymentFactor, carrying);
  const purchasePrice = Math.max(downPayment, Math.min(rawPrice, maxPriceForDownPayment(downPayment)));
  const insurancePremium = mortgageInsurance(purchasePrice, downPayment).premium;
  const mortgage = Math.max(0, purchasePrice - downPayment) + insurancePremium;
  const qualifyingPayment = amortizedPayment(mortgage, qualifyingRate, amortizationMonths);
  const housingPayment = qualifyingHousingCost({
    principalAndInterest: qualifyingPayment,
    propertyTaxMonthly: (purchasePrice * carrying.propertyTaxAnnualRate) / 12,
    heatMonthly: carrying.heatMonthly,
    strataMonthly: carrying.strataMonthly,
  });

  return {
    purchasePrice,
    mortgage,
    insurancePremium,
    qualifyingPayment,
    housingPayment,
    boundBy,
    contractRate,
    qualifyingRate,
    amortizationMonths,
    carrying,
  };
}

/** The sentence printed under every figure, so no number appears unexplained. */
export function ceilingAssumptionLine(ceiling: LenderCeiling): string {
  const pct = (rate: number) => `${(rate * 100).toFixed(2).replace(/\.00$/, '')}%`;
  const years = Math.round(ceiling.amortizationMonths / 12);
  const taxPct = `${(ceiling.carrying.propertyTaxAnnualRate * 100).toFixed(2)}%`;
  return (
    `Uses a ${pct(ceiling.contractRate)} rate and the ${pct(ceiling.qualifyingRate)} stress-test rate, paid off over ${years} years. ` +
    `Counts half of a $${ceiling.carrying.strataMonthly} strata fee, ` +
    `$${ceiling.carrying.heatMonthly} for heat and ${taxPct} property tax. ` +
    `Home costs stay under ${Math.round(GDS_RATIO * 100)}% of your income. ` +
    (ceiling.insurancePremium > 0
      ? `With under 20% down, $${Math.round(ceiling.insurancePremium).toLocaleString('en-CA')} of insurance is added to the loan. `
      : '') +
    `${ceiling.boundBy === 'tds' ? 'Your other debt payments lower this.' : 'No other debts.'} ` +
    'Not a pre-approval.'
  );
}

export { GDS_RATIO, TDS_RATIO, ILLUSTRATIVE_CONTRACT_RATE, TYPICAL_CONTRACT_RATE };
